/* ============================================
   YOBHA Shopify Theme — Main JS
   ============================================ */

'use strict';

// ── Toast ──────────────────────────────────────
const Toast = (() => {
  let container;
  function getContainer() {
    if (!container) {
      container = document.createElement('div');
      container.className = 'toast-container';
      document.body.appendChild(container);
    }
    return container;
  }
  function show(msg, type = 'success', duration = 3000) {
    const el = document.createElement('div');
    el.className = `toast toast--${type}`;
    el.textContent = msg;
    getContainer().appendChild(el);
    setTimeout(() => { el.style.opacity = '0'; el.style.transition = 'opacity 0.3s'; }, duration);
    setTimeout(() => el.remove(), duration + 350);
  }
  return { success: m => show(m,'success'), error: m => show(m,'error'), info: m => show(m,'info') };
})();

// ── Cart count ─────────────────────────────────
function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
    if (count > 0) {
      el.classList.add('cart-badge-animate');
      setTimeout(() => el.classList.remove('cart-badge-animate'), 600);
    }
  });
}

async function fetchCartCount() {
  try {
    const r = await fetch('/cart.js');
    const cart = await r.json();
    updateCartCount(cart.item_count);
  } catch(e) {}
}

// ── AJAX cart add ──────────────────────────────
async function addToCart(variantId, quantity = 1, properties = {}) {
  try {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    if (!r.ok) throw new Error('Add to cart failed');
    const item = await r.json();
    await fetchCartCount();
    Toast.success('Added to bag');
    return item;
  } catch(e) {
    Toast.error('Could not add to bag. Please try again.');
    throw e;
  }
}

// ── Update cart quantity ───────────────────────
async function updateCartItem(key, quantity) {
  const r = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  });
  const cart = await r.json();
  updateCartCount(cart.item_count);
  return cart;
}

async function removeCartItem(key) {
  return updateCartItem(key, 0);
}

// ── Hero Slider ────────────────────────────────
class HeroSlider {
  constructor(el) {
    this.el = el;
    this.slides = el.querySelectorAll('.hero-slider__slide');
    this.dots = el.querySelectorAll('.hero-slider__dot');
    this.current = 0;
    this.total = this.slides.length;
    this.timer = null;
    this.hovered = false;
    if (this.total <= 1) return;
    this.bind();
    this.start();
  }
  go(i) {
    const oldIdx = this.current;
    const next = (i + this.total) % this.total;
    if (oldIdx === next) return;
    this.current = next;

    const oldSlide = this.slides[oldIdx];
    const newSlide = this.slides[this.current];

    oldSlide.classList.add('is-leaving');
    oldSlide.classList.remove('is-active');
    newSlide.classList.add('is-active');
    setTimeout(() => oldSlide.classList.remove('is-leaving'), 500);

    const oldDot = this.dots[oldIdx];
    if (oldDot) oldDot.classList.remove('is-active');
    const newDot = this.dots[this.current];
    if (newDot) { newDot.classList.remove('is-active'); void newDot.offsetWidth; newDot.classList.add('is-active'); }
  }
  start() {
    clearInterval(this.timer);
    this.timer = setInterval(() => { if (!this.hovered) this.go(this.current + 1); }, 4500);
  }
  bind() {
    this.dots.forEach((d, i) => d.addEventListener('click', () => { this.go(i); this.start(); }));
    this.el.addEventListener('mouseenter', () => { this.hovered = true; });
    this.el.addEventListener('mouseleave', () => { this.hovered = false; });
    this.slides.forEach(s => {
      s.addEventListener('click', () => { const href = s.dataset.href; if (href) window.location.href = href; });
    });
    // Touch swipe
    let tsx = 0, tsy = 0, tDragging = false;
    this.el.addEventListener('touchstart', e => {
      tsx = e.touches[0].clientX; tsy = e.touches[0].clientY; tDragging = false;
    }, { passive: true });
    this.el.addEventListener('touchmove', e => {
      const dx = e.touches[0].clientX - tsx;
      const dy = e.touches[0].clientY - tsy;
      if (!tDragging && Math.abs(dy) > Math.abs(dx)) return;
      tDragging = true;
      e.preventDefault();
    }, { passive: false });
    this.el.addEventListener('touchend', e => {
      if (!tDragging) return;
      const dx = e.changedTouches[0].clientX - tsx;
      if (Math.abs(dx) > 40) this.go(dx < 0 ? this.current + 1 : this.current - 1);
      tDragging = false;
    }, { passive: true });
  }
}

// ── Sidebar ────────────────────────────────────
class Sidebar {
  constructor() {
    this.sidebar  = document.getElementById('site-sidebar');
    this.overlay  = document.getElementById('sidebar-overlay');
    this.openBtns = document.querySelectorAll('[data-sidebar-open]');
    this.closeBtn = document.getElementById('sidebar-close');
    this.bind();
  }
  open()  {
    this.sidebar?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }
  close() {
    this.sidebar?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    this.sidebar?.querySelectorAll('.sidebar-submenu').forEach(s => s.classList.remove('is-open'));
    document.body.style.overflow = '';
  }
  bind() {
    this.openBtns.forEach(b => b.addEventListener('click', () => this.open()));
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click',  () => this.close());
    this.sidebar?.querySelectorAll('[data-sidebar-submenu-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.sidebarSubmenuToggle;
        document.getElementById(id)?.classList.add('is-open');
      });
    });
    this.sidebar?.querySelectorAll('[data-sidebar-submenu-back]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.sidebarSubmenuBack;
        document.getElementById(id)?.classList.remove('is-open');
      });
    });
    this.sidebar?.querySelectorAll('[data-sidebar-close]').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
    this.sidebar?.querySelectorAll('a').forEach(a => a.addEventListener('click', () => this.close()));
  }
}

// ── Search Drawer ──────────────────────────────
class SearchDrawer {
  constructor() {
    this.drawer  = document.getElementById('search-drawer');
    this.input   = document.getElementById('search-input');
    this.results = document.getElementById('search-results');
    this.openBtns= document.querySelectorAll('[data-search-open]');
    this.closeBtn= document.getElementById('search-close');
    this.debounceTimer = null;
    this.bind();
  }
  open()  { this.drawer?.classList.add('is-open'); this.input?.focus(); }
  close() {
    this.drawer?.classList.remove('is-open');
    if (this.input) this.input.value = '';
    this.results?.classList.remove('is-visible');
    if (this.results) this.results.innerHTML = '';
  }
  async search(q) {
    if (!q.trim()) { this.results?.classList.remove('is-visible'); return; }
    try {
      const r = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=8`);
      const data = await r.json();
      const products = (data.resources?.results?.products || []).filter(p => !p.tags?.includes('gift-wrap'));
      const collections = data.resources?.results?.collections || [];
      this.renderResults(products, collections, q);
    } catch(e) {}
  }
  renderResults(products, collections, q) {
    if (!this.results) return;
    if (!products.length && !collections.length) {
      this.results.innerHTML = `<div style="padding:1.5rem;text-align:center;font-size:0.875rem;font-weight:300;color:#6b7280;">No results found for "${q}"</div>`;
      this.results.classList.add('is-visible');
      return;
    }
    let html = '';
    if (collections.length) {
      html += `<div class="search-result-section-label">Collections</div>`;
      html += collections.map(c => {
        const thumb = c.image?.url || c.featured_image?.url;
        const iconHtml = thumb
          ? `<img src="${thumb}" alt="${c.title}">`
          : `<div class="search-result-item__collection-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg></div>`;
        return `
        <a href="${c.url}" class="search-result-item search-result-item--collection">
          ${iconHtml}
          <div>
            <div class="search-result-item__name">${c.title}</div>
            <div class="search-result-item__type">Collection</div>
          </div>
        </a>`;
      }).join('');
    }
    if (products.length) {
      if (collections.length) html += `<div class="search-result-section-label">Products</div>`;
      html += products.map(p => {
        const price = p.price != null ? '₹' + Number(p.price).toLocaleString('en-IN') : '';
        return `
        <a href="${p.url}" class="search-result-item">
          <img src="${p.featured_image?.url || ''}" alt="${p.title}" loading="lazy">
          <div>
            <div class="search-result-item__name">${p.title}</div>
            ${price ? `<div class="search-result-item__price">${price}</div>` : ''}
          </div>
        </a>`;
      }).join('');
    }
    this.results.innerHTML = html;
    this.results.classList.add('is-visible');
  }
  bind() {
    this.openBtns.forEach(b => b.addEventListener('click', e => { e.stopPropagation(); this.open(); }));
    this.closeBtn?.addEventListener('click', () => this.close());
    this.input?.addEventListener('input', e => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.search(e.target.value), 300);
    });
    document.addEventListener('click', e => {
      if (this.drawer?.classList.contains('is-open') && !this.drawer.contains(e.target) && !e.target.closest('[data-search-open]')) {
        this.close();
      }
    });
  }
}

// ── Account Dropdown ───────────────────────────
class AccountDropdown {
  constructor() {
    this.toggle   = document.getElementById('account-toggle');
    this.dropdown = document.getElementById('account-dropdown');
    this.bind();
  }
  bind() {
    this.toggle?.addEventListener('click', e => { e.stopPropagation(); this.dropdown?.classList.toggle('is-open'); });
    document.addEventListener('click', e => {
      if (!e.target.closest('#account-dropdown') && !e.target.closest('#account-toggle')) {
        this.dropdown?.classList.remove('is-open');
      }
    });
  }
}

// ── Product Card image carousel ────────────────
class ProductCard {
  constructor(el) {
    this.el     = el;
    this.images = el.querySelectorAll('.product-card__image');
    this.current= 0;
    this.timer  = null;

    // Wishlist
    const wishBtn = el.querySelector('[data-wishlist-btn]');
    if (wishBtn) {
      this._syncCardWishlist(wishBtn);
      wishBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = String(wishBtn.dataset.variantId || '');
        let items;
        try { items = JSON.parse(localStorage.getItem('yobha_wishlist') || '[]'); } catch(e) { items = []; }
        const idx = items.findIndex(i => String(i.variantId) === variantId);
        if (idx >= 0) {
          items.splice(idx, 1);
          wishBtn.classList.remove('is-wishlisted');
          if (typeof Toast !== 'undefined') Toast.info('Removed from wishlist');
        } else {
          items.push({
            variantId,
            handle: wishBtn.dataset.handle || '',
            title:  wishBtn.dataset.title  || '',
            price:  wishBtn.dataset.price  || '',
            image:  wishBtn.dataset.image  || '',
            color:  '',
            url:    wishBtn.dataset.url    || ''
          });
          wishBtn.classList.add('is-wishlisted');
          if (typeof Toast !== 'undefined') Toast.success('Added to wishlist');
        }
        localStorage.setItem('yobha_wishlist', JSON.stringify(items));
        if (typeof updateWishlistCount === 'function') updateWishlistCount();
      });
    }

    if (this.images.length <= 1) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { el.classList.add('is-visible'); obs.unobserve(el); }
    }, { threshold: 0.1, rootMargin: '50px' });
    obs.observe(el);
    el.addEventListener('mouseenter', () => this.startCarousel());
    el.addEventListener('mouseleave', () => this.stopCarousel());
    el.addEventListener('touchstart', () => this.startCarousel(), { passive: true });
    el.addEventListener('touchend',   () => setTimeout(() => this.stopCarousel(), 3000), { passive: true });
  }
  _syncCardWishlist(btn) {
    try {
      const items = JSON.parse(localStorage.getItem('yobha_wishlist') || '[]');
      const vid = String(btn.dataset.variantId || '');
      btn.classList.toggle('is-wishlisted', items.some(i => String(i.variantId) === vid));
    } catch(e) {}
  }
  startCarousel() { if (this.timer) return; this.timer = setInterval(() => this.next(), 2000); }
  stopCarousel()  {
    clearInterval(this.timer); this.timer = null;
    this.images.forEach((img, i) => {
      img.style.opacity = i === 0 ? '1' : '0';
      img.classList.toggle('product-card__image--active', i === 0);
    });
    this.current = 0;
  }
  next() {
    this.images[this.current].style.opacity = '0';
    this.images[this.current].classList.remove('product-card__image--active');
    this.current = (this.current + 1) % this.images.length;
    this.images[this.current].style.opacity = '1';
    this.images[this.current].classList.add('product-card__image--active');
  }
}

// ── Product Page ───────────────────────────────
class ProductPage {
  constructor() {
    this.form         = document.getElementById('product-form');
    this.addBtn       = document.getElementById('product-add-btn');
    this.buyBtn       = document.getElementById('product-buy-btn');
    this.priceEl      = document.getElementById('product-price');
    this.comparePriceEl= document.getElementById('product-compare-price');
    this.variants     = Array.isArray(window.__productVariants)    ? window.__productVariants    : [];
    this.optionNames  = Array.isArray(window.__productOptionNames) ? window.__productOptionNames : [];
    this.selectedOptions = {};

    // Option buttons
    this.colorPills  = document.querySelectorAll('.pi-swatch');
    this.genericVals = document.querySelectorAll('.pi-generic-value');
    this.sizeItems   = document.querySelectorAll('.pi-size-btn');

    // Desktop thumbnails + main image slides
    this.thumbs       = document.querySelectorAll('.pmd-thumb');
    this.desktopSlides= document.querySelectorAll('.pmd-main__slide');

    // Mobile slider
    this.mobileTrack      = document.getElementById('pms-track');
    this.mobileCurrentIdx = 0;
    this.colorRange       = { start: 0, end: Math.max(0, document.querySelectorAll('.pms-slide').length - 1) };

    // Wishlist
    this.wishlistBtn = document.getElementById('pi-wishlist');

    // Build color → image index map using variant featured_image data
    this._colorImageMap = this._buildColorImageMap();

    // Init selected options from active elements
    this._initSelectedOptions();

    // Init from URL ?variant=ID
    this._initFromUrl();

    // Bind all interactions
    this._bindColorPills();
    this._bindGenericValues();
    this._bindSizeBtns();
    this._bindThumbs();
    this._bindMobileSlider();
    this._bindAddToCart();
    this._bindBuyNow();
    this._bindWishlist();
    this._initSwatchColors();

    // Show images for whichever swatch is active
    const activeSwatch = document.querySelector('.pi-swatch.is-active');
    this._syncImagesToColor(activeSwatch ? parseInt(activeSwatch.dataset.colorIndex || '0', 10) : 0);
  }

  // ── Build color → [slideIndices] map using variant.featured_image ──
  _buildColorImageMap() {
    const colors = Array.isArray(window.__productColors) ? window.__productColors : [];
    const total  = document.querySelectorAll('.pms-slide').length;
    if (!colors.length || !total) return {};

    const colorOptionIdx = this.optionNames.findIndex(n => n === 'Color' || n === 'Colour');
    const allSlides = Array.from(document.querySelectorAll('.pms-slide'));
    const imageIds  = allSlides.map(s => parseInt(s.dataset.imageId || '0', 10));

    // For each color, find the featured_image id of the first variant with that color
    const colorStartIds = colors.map(color => {
      const colorLower = color.toLowerCase().trim();
      const variant = this.variants.find(v => {
        const val = colorOptionIdx >= 0 ? v[`option${colorOptionIdx + 1}`] : null;
        return val && val.toLowerCase().trim() === colorLower;
      });
      return (variant && variant.featured_image) ? variant.featured_image.id : null;
    });

    // Convert ids to slide positions
    const colorStarts = colorStartIds.map(id => (id !== null ? imageIds.indexOf(id) : -1));

    // Build map
    const map = {};
    colors.forEach((_, ci) => {
      const start = colorStarts[ci] >= 0 ? colorStarts[ci] : ci * 4;
      let end = total;
      for (let j = ci + 1; j < colors.length; j++) {
        if (colorStarts[j] > start) { end = colorStarts[j]; break; }
      }
      map[ci] = Array.from({ length: end - start }, (_, k) => start + k);
    });

    // If only 1 color or map is empty, all images belong to first group
    if (!Object.keys(map).length) map[0] = Array.from({ length: total }, (_, i) => i);
    return map;
  }

  // ── Pre-select from active buttons ──
  _initSelectedOptions() {
    this.colorPills.forEach(pill => {
      if (pill.classList.contains('is-active')) {
        this.selectedOptions[pill.dataset.optionName] = pill.dataset.optionValue;
      }
    });
    this.sizeItems.forEach(item => {
      if (item.classList.contains('is-active')) {
        this.selectedOptions[item.dataset.optionName] = item.dataset.optionValue;
      }
    });
    this.genericVals.forEach(btn => {
      if (btn.classList.contains('is-active')) {
        this.selectedOptions[btn.dataset.optionName] = btn.dataset.optionValue;
      }
    });
  }

  // ── Read ?variant=ID from URL, pre-select matching options ──
  _initFromUrl() {
    const params  = new URLSearchParams(window.location.search);
    const varId   = params.get('variant');
    const colorParam = params.get('color');

    if (varId) {
      const variant = this.variants.find(v => String(v.id) === varId);
      if (variant) {
        this.optionNames.forEach((name, i) => {
          const val = variant[`option${i + 1}`];
          if (val) {
            this.selectedOptions[name] = val;
            // Activate color swatch + write ?color= into URL
            this.colorPills.forEach(pill => {
              if (pill.dataset.optionName === name && pill.dataset.optionValue === val) {
                this.colorPills.forEach(p => p.classList.remove('is-active'));
                pill.classList.add('is-active');
                const colorLabel = document.getElementById('selected-color-label');
                if (colorLabel) colorLabel.textContent = val;
                const idx = parseInt(pill.dataset.colorIndex || '0', 10);
                this._syncImagesToColor(idx);
                params.set('color', val);
                window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
              }
            });
            // Activate size button
            this.sizeItems.forEach(item => {
              if (item.dataset.optionName === name && item.dataset.optionValue === val) {
                this.sizeItems.forEach(s => s.classList.remove('is-active'));
                item.classList.add('is-active');
              }
            });
            // Activate generic
            this.genericVals.forEach(btn => {
              if (btn.dataset.optionName === name && btn.dataset.optionValue === val) {
                const group = btn.closest('.pi-generic-values');
                group?.querySelectorAll('.pi-generic-value').forEach(b => b.classList.remove('is-active'));
                btn.classList.add('is-active');
              }
            });
          }
        });
        this._updateVariant();
        return;
      }
    }

    // Fallback: ?color=Name
    if (colorParam) {
      const pill = Array.from(this.colorPills).find(
        p => p.dataset.optionValue.toLowerCase() === colorParam.toLowerCase()
      );
      if (pill) {
        this.colorPills.forEach(p => p.classList.remove('is-active'));
        pill.classList.add('is-active');
        this.selectedOptions[pill.dataset.optionName] = pill.dataset.optionValue;
        const colorLabel = document.getElementById('selected-color-label');
        if (colorLabel) colorLabel.textContent = pill.dataset.optionValue;
        this._syncImagesToColor(parseInt(pill.dataset.colorIndex || '0', 10));
        this._updateVariant();
      }
    }
  }

  // ── Show only images for this color index ──
  _syncImagesToColor(colorIndex) {
    const visibleIndices = this._colorImageMap[colorIndex] ||
      Array.from({ length: document.querySelectorAll('.pms-slide').length }, (_, i) => i);
    const start = visibleIndices[0] ?? 0;
    const end   = visibleIndices[visibleIndices.length - 1] ?? 0;

    // Desktop thumbs
    let firstVisibleThumb = null;
    this.thumbs.forEach(thumb => {
      const idx = parseInt(thumb.dataset.imgIndex, 10);
      const visible = visibleIndices.includes(idx);
      thumb.style.display = visible ? '' : 'none';
      if (visible && !firstVisibleThumb) firstVisibleThumb = thumb;
    });
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    if (firstVisibleThumb) {
      firstVisibleThumb.classList.add('is-active');
      this._showDesktopSlide(parseInt(firstVisibleThumb.dataset.imgIndex, 10));
    }

    // Mobile: set nav range so arrows/swipe stay within this color's images
    this.colorRange = { start, end };

    // Show only this color's dots
    document.querySelectorAll('.pms-dot').forEach((dot, i) => {
      dot.style.display = visibleIndices.includes(i) ? '' : 'none';
    });

    // Go to first image of this color
    this._goToMobileSlide(start);
  }

  _showDesktopSlide(imgIndex) {
    this.desktopSlides.forEach(slide => {
      const match = parseInt(slide.dataset.imgIndex, 10) === imgIndex;
      slide.classList.toggle('is-active', match);
    });
  }

  // ── Mobile slider ──
  _goToMobileSlide(index) {
    const slides = document.querySelectorAll('.pms-slide');
    if (!slides.length || !this.mobileTrack) return;
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    this.mobileCurrentIdx = clamped;
    this.mobileTrack.style.transform = `translateX(-${clamped * 100}%)`;
    document.querySelectorAll('.pms-dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === clamped);
    });
  }

  _bindMobileSlider() {
    const slides = document.querySelectorAll('.pms-slide');
    if (slides.length <= 1) return;

    document.getElementById('pms-prev')?.addEventListener('click', () => {
      const prev = this.mobileCurrentIdx <= this.colorRange.start
        ? this.colorRange.end
        : this.mobileCurrentIdx - 1;
      this._goToMobileSlide(prev);
    });
    document.getElementById('pms-next')?.addEventListener('click', () => {
      const next = this.mobileCurrentIdx >= this.colorRange.end
        ? this.colorRange.start
        : this.mobileCurrentIdx + 1;
      this._goToMobileSlide(next);
    });
    document.querySelectorAll('.pms-dot').forEach((dot, i) => {
      dot.addEventListener('click', () => this._goToMobileSlide(i));
    });

    const track = this.mobileTrack;
    if (!track) return;

    let touchStartX = 0, touchStartY = 0, isDragging = false;
    const THRESHOLD = 40;

    track.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isDragging = false;
      track.style.transition = 'none';
    }, { passive: true });

    track.addEventListener('touchmove', e => {
      const dx = e.touches[0].clientX - touchStartX;
      const dy = e.touches[0].clientY - touchStartY;
      if (!isDragging && Math.abs(dy) > Math.abs(dx)) {
        track.style.transition = '';
        return;
      }
      isDragging = true;
      e.preventDefault();
      const baseOffset = this.mobileCurrentIdx * track.offsetWidth;
      const clamp = Math.max(-track.offsetWidth * 0.75, Math.min(track.offsetWidth * 0.75, dx));
      track.style.transform = `translateX(${-(baseOffset - clamp)}px)`;
    }, { passive: false });

    track.addEventListener('touchend', e => {
      track.style.transition = '';
      if (!isDragging) return;
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > THRESHOLD) {
        if (dx < 0 && this.mobileCurrentIdx < this.colorRange.end) {
          this._goToMobileSlide(this.mobileCurrentIdx + 1);
        } else if (dx > 0 && this.mobileCurrentIdx > this.colorRange.start) {
          this._goToMobileSlide(this.mobileCurrentIdx - 1);
        } else {
          this._goToMobileSlide(this.mobileCurrentIdx);
        }
      } else {
        this._goToMobileSlide(this.mobileCurrentIdx);
      }
      isDragging = false;
    }, { passive: true });
  }

  // ── Desktop thumbnails ──
  _bindThumbs() {
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        this.thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        const imgIdx = parseInt(thumb.dataset.imgIndex, 10);
        this._showDesktopSlide(imgIdx);
      });
    });
  }

  // ── Color swatches ──
  _bindColorPills() {
    this.colorPills.forEach(swatch => {
      swatch.addEventListener('click', () => {
        if (swatch.classList.contains('is-active')) return;
        this.colorPills.forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');
        const name = swatch.dataset.optionName;
        const val  = swatch.dataset.optionValue;
        const idx  = parseInt(swatch.dataset.colorIndex || '0', 10);
        this.selectedOptions[name] = val;
        const label = document.getElementById('selected-color-label');
        if (label) label.textContent = val;
        this._syncImagesToColor(idx);
        this._updateVariant();
      });
    });
  }

  // ── Size button grid ──
  _bindSizeBtns() {
    this.sizeItems.forEach(btn => {
      btn.addEventListener('click', () => {
        this.sizeItems.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        this.selectedOptions[btn.dataset.optionName] = btn.dataset.optionValue;
        this._updateVariant();
      });
    });
  }

  // ── Generic option values ──
  _bindGenericValues() {
    this.genericVals.forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.closest('.pi-generic-values');
        group?.querySelectorAll('.pi-generic-value').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        this.selectedOptions[btn.dataset.optionName] = btn.dataset.optionValue;
        this._updateVariant();
      });
    });
  }

  // ── Set swatch background colors — JS fallback for colors not caught by Liquid map ──
  _initSwatchColors() {
    const MAP = {
      white:'#ffffff', ivory:'#fffff0', cream:'#faf6f2',
      black:'#1a1a1a', charcoal:'#374151',
      pink:'#f9a8d4', blush:'#ffc0cb', rose:'#f43f5e',
      red:'#ef4444', burgundy:'#800020',
      blue:'#3b82f6', navy:'#1e3a5f', teal:'#0d9488',
      green:'#16a34a', sage:'#8faf8f', mint:'#abebc6',
      grey:'#9ca3af', gray:'#9ca3af', silver:'#d1d5db',
      beige:'#f5deb3', nude:'#e8c9a0', camel:'#c19a6b',
      brown:'#92400e', purple:'#7c3aed', lavender:'#c4b5fd',
      yellow:'#fbbf24', gold:'#c9a96e', orange:'#f97316', peach:'#ffb38a',
    };
    document.querySelectorAll('.pi-swatch').forEach(swatch => {
      // Liquid already set a solid color — skip
      if (swatch.style.backgroundColor) return;
      const raw = (swatch.dataset.colorName || '').toLowerCase().replace(/[\s-]/g, '_');
      let color = MAP[raw];
      if (!color) {
        for (const [key, val] of Object.entries(MAP)) {
          if (raw.includes(key.replace(/\s/g,'_'))) { color = val; break; }
        }
      }
      if (color) {
        swatch.style.backgroundColor = color;
        const light = ['white','ivory','cream','silver','beige','nude','yellow','gold','mint','peach','lavender','blush','pink'];
        if (light.some(c => raw.includes(c))) swatch.style.borderColor = '#c4c4c4';
      }
    });
  }

  // ── Variant lookup + UI update ──
  _updateVariant() {
    const variant = this._findVariant();
    if (!variant) return;

    const variantId = variant.id;

    // Update form + buttons
    if (this.addBtn) {
      this.addBtn.dataset.variantId = variantId;
      this.addBtn.disabled = !variant.available;
      this.addBtn.textContent = variant.available ? (this.addBtn.dataset.addText || 'Add to Bag') : 'Sold Out';
    }
    if (this.buyBtn) {
      this.buyBtn.dataset.variantId = variantId;
      this.buyBtn.style.display = variant.available ? '' : 'none';
    }
    const formVariant = document.getElementById('product-form-variant-id');
    if (formVariant) formVariant.value = variantId;

    // Update price
    if (this.priceEl) this.priceEl.textContent = this._formatMoney(variant.price);
    if (this.comparePriceEl) {
      if (variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = this._formatMoney(variant.compare_at_price);
        this.comparePriceEl.style.display = '';
      } else {
        this.comparePriceEl.style.display = 'none';
      }
    }

    // Update URL — variant first, color always last for consistency
    const params = new URLSearchParams(window.location.search);
    const activePillColor = document.querySelector('.pi-swatch.is-active')?.dataset.optionValue;
    const colorVal = activePillColor || params.get('color');
    params.delete('variant');
    params.delete('color');
    params.set('variant', variantId);
    if (colorVal) params.set('color', colorVal);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }

  _findVariant() {
    return this.variants.find(v =>
      Object.entries(this.selectedOptions).every(([name, val]) => {
        const i = this.optionNames.indexOf(name);
        return i < 0 || v[`option${i + 1}`] === val;
      })
    ) || this.variants[0];
  }

  _formatMoney(cents) {
    return '₹ ' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  // ── Add to Cart ──
  _bindAddToCart() {
    this.addBtn?.addEventListener('click', async () => {
      const variantId = this.addBtn.dataset.variantId;
      if (!variantId) { Toast.error('Please select a size'); return; }
      this.addBtn.disabled = true;
      const original = this.addBtn.textContent;
      this.addBtn.innerHTML = '<span style="display:inline-flex;align-items:center;gap:6px"><svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="animation:spin 0.7s linear infinite"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg> Adding…</span>';
      try {
        await addToCart(parseInt(variantId));
        this.addBtn.textContent = 'Added!';
        setTimeout(() => {
          this.addBtn.textContent = original;
          this.addBtn.disabled = false;
        }, 2000);
      } catch(e) {
        this.addBtn.textContent = original;
        this.addBtn.disabled = false;
      }
    });
  }

  // ── Buy Now ──
  _bindBuyNow() {
    this.buyBtn?.addEventListener('click', async () => {
      const variantId = this.buyBtn.dataset.variantId;
      if (!variantId) return;
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        if (cart.items?.length) {
          sessionStorage.setItem('yobha_saved_cart', JSON.stringify(
            cart.items.map(i => ({ id: i.variant_id, quantity: i.quantity }))
          ));
          sessionStorage.setItem('yobha_buy_now_variant', String(variantId));
        }
      } catch(e) {}
      window.location.href = `/cart/${variantId}:1?checkout`;
    });
  }

  // ── Wishlist (localStorage) ──
  _getWishlist() {
    try { return JSON.parse(localStorage.getItem('yobha_wishlist') || '[]'); } catch(e) { return []; }
  }
  _saveWishlist(items) {
    localStorage.setItem('yobha_wishlist', JSON.stringify(items));
  }
  _buildWishlistItem() {
    const variant = this._findVariant();
    const variantId = variant?.id || this.form?.querySelector('[name="id"]')?.value || '';
    const handle = window.location.pathname.split('/products/')[1]?.split('?')[0] || '';
    const titleEl = document.querySelector('.pi-name');
    const colorPill = document.querySelector('.pi-swatch.is-active');
    const colorName = colorPill?.dataset.optionValue || '';
    const image = variant?.featured_image?.src
      || document.querySelector('.pmd-main__slide img, .pms-slide img')?.src
      || '';
    return {
      variantId: String(variantId),
      handle,
      title: titleEl?.textContent?.trim() || document.title,
      price: this.priceEl?.textContent?.trim() || '',
      image,
      color: colorName,
      url: window.location.pathname + (variantId ? '?variant=' + variantId + (colorName ? '&color=' + encodeURIComponent(colorName) : '') : '')
    };
  }
  _syncWishlistBtn() {
    if (!this.wishlistBtn) return;
    const variantId = this.form?.querySelector('[name="id"]')?.value;
    const wishlisted = this._getWishlist().some(i => String(i.variantId) === String(variantId));
    this.wishlistBtn.classList.toggle('is-wishlisted', wishlisted);
  }
  _bindWishlist() {
    this._syncWishlistBtn();
    this.wishlistBtn?.addEventListener('click', () => {
      const item = this._buildWishlistItem();
      let items = this._getWishlist();
      const idx = items.findIndex(i => String(i.variantId) === String(item.variantId));
      if (idx >= 0) {
        items.splice(idx, 1);
        this.wishlistBtn.classList.remove('is-wishlisted');
        Toast.info('Removed from wishlist');
      } else {
        items.push(item);
        this.wishlistBtn.classList.add('is-wishlisted');
        Toast.success('Added to wishlist');
      }
      this._saveWishlist(items);
      if (typeof updateWishlistCount === 'function') updateWishlistCount();
    });
  }
}

// ── Cart Page ──────────────────────────────────
class CartPage {
  constructor() {
    this.wrap = document.getElementById('cart-items-wrap');
    if (!this.wrap) return;
    this.bindQty();
    this.bindRemove();
  }
  async removeLinkedGiftWrap(productTitle) {
    if (!productTitle) return;
    const cart = await fetch('/cart.js').then(r => r.json());
    const linked = cart.items.filter(i =>
      i.handle === 'gift-wrap-1' && i.properties && i.properties['For'] === productTitle
    );
    for (const gw of linked) {
      await removeCartItem(gw.key);
      const gwEl = document.querySelector(`.cart-item[data-key="${gw.key}"]`);
      if (gwEl) gwEl.remove();
    }
  }
  bindQty() {
    document.querySelectorAll('[data-cart-qty-btn]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key      = btn.dataset.key;
        const delta    = parseInt(btn.dataset.delta);
        const numEl    = btn.closest('.cart-item__qty').querySelector('.cart-item__qty-num');
        const cartItem = btn.closest('.cart-item');
        const currentQty = parseInt(numEl.textContent);
        const newQty     = Math.max(0, currentQty + delta);
        try {
          const cart = await updateCartItem(key, newQty);
          if (newQty === 0) {
            const productTitle = cartItem?.querySelector('.cart-item__name')?.textContent?.trim();
            cartItem.remove();
            await this.removeLinkedGiftWrap(productTitle);
          } else {
            numEl.textContent = newQty;
          }
          const updatedCart = newQty === 0 ? await fetch('/cart.js').then(r => r.json()) : cart;
          this.updateTotals(updatedCart);
          if (updatedCart.item_count === 0) location.reload();
        } catch(e) { Toast.error('Could not update cart.'); }
      });
    });
  }
  bindRemove() {
    document.querySelectorAll('[data-cart-remove]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const cartItem = btn.closest('.cart-item');
        const productTitle = cartItem?.querySelector('.cart-item__name')?.textContent?.trim();
        try {
          await removeCartItem(key);
          cartItem.remove();
          await this.removeLinkedGiftWrap(productTitle);
          const updatedCart = await fetch('/cart.js').then(r => r.json());
          this.updateTotals(updatedCart);
          if (updatedCart.item_count === 0) location.reload();
        } catch(e) { Toast.error('Could not remove item.'); }
      });
    });
  }
  updateTotals(cart) {
    const fmt = c => '₹ ' + (c / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const totalEl    = document.getElementById('cart-total');
    const subtotalEl = document.getElementById('cart-subtotal');
    if (totalEl)    totalEl.textContent    = fmt(cart.total_price);
    if (subtotalEl) subtotalEl.textContent = fmt(cart.items_subtotal_price ?? cart.subtotal_price ?? cart.total_price);
    updateCartCount(cart.item_count);
    // Update subtitle
    const subtitleEl = document.querySelector('.cart-page__subtitle');
    if (subtitleEl) {
      const n = cart.item_count;
      subtitleEl.textContent = n === 0 ? '0 items in your cart' : n === 1 ? '1 item in your cart' : `${n} items in your cart`;
    }
    const summarySubtitle = document.querySelector('.cart-summary__subtitle');
    if (summarySubtitle) summarySubtitle.textContent = `${cart.item_count} item${cart.item_count !== 1 ? 's' : ''} selected`;
  }
}

// ── Hot Picks via Product Recommendations API ──
function initHotPicks() {
  const section = document.getElementById('hot-picks-section');
  if (!section) return;
  const productId = section.dataset.productId;
  const collectionHandle = section.dataset.collectionHandle;
  if (!productId) return;

  function fmtMoney(cents) {
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  function renderProducts(products) {
    const filtered = products.filter(p => String(p.id) !== String(productId)).slice(0, 8);
    if (!filtered.length) return;
    const track = document.getElementById('hp-track');
    if (!track) return;
    track.innerHTML = filtered.map(p => {
      const img = p.image || (p.images && p.images[0] ? (p.images[0].src || p.images[0]) : '') || (p.featured_image ? (p.featured_image.src || p.featured_image) : '');
      const url = '/products/' + p.handle;
      const price = p.price_min || p.price || 0;
      const compare = p.compare_at_price_max || p.compare_at_price || 0;
      const hasDiscount = compare > price;
      const pct = hasDiscount ? Math.round((compare - price) * 100 / compare) : 0;
      return `<div class="hot-picks__card">
        <div class="product-card" data-product-card>
          <a href="${url}" style="display:contents;">
            <div class="product-card__image-wrap">
              ${img ? `<img class="product-card__image product-card__image--active" src="${img}" alt="${p.title}" loading="lazy">` : ''}
            </div>
          </a>
          <a href="${url}" style="display:contents;">
            <div class="product-card__info">
              <h3 class="product-card__name">${p.title}</h3>
              <div class="product-card__price">
                ${hasDiscount
                  ? `<div><span class="product-card__badge">${pct}% Off</span></div><div class="product-card__price-row"><span class="product-card__price-current">${fmtMoney(price)}</span><span class="product-card__price-compare">${fmtMoney(compare)}</span></div>`
                  : `<span class="product-card__price-current">${fmtMoney(price)}</span>`}
              </div>
            </div>
          </a>
        </div>
      </div>`;
    }).join('');
    section.style.display = '';
    requestAnimationFrame(() => {
      track.querySelectorAll('.product-card').forEach(el => new ProductCard(el));
      new HotPicksSlider();
    });
  }

  // Use Liquid-prebuilt data first (has proper Shopify CDN image URLs)
  if (window.__relatedProducts && window.__relatedProducts.length >= 1) {
    renderProducts(window.__relatedProducts);
    return;
  }

  // Fallback: Shopify recommendations API
  fetch(`/recommendations/products.json?product_id=${productId}&limit=8`)
    .then(r => r.json())
    .then(d => renderProducts(d.products || []))
    .catch(() => {});
}

// ── Hot Picks Slider ───────────────────────────
class HotPicksSlider {
  constructor() {
    this.track   = document.getElementById('hp-track');
    this.prevBtn = document.getElementById('hp-prev');
    this.nextBtn = document.getElementById('hp-next');
    if (!this.track) return;
    this.cards     = this.track.querySelectorAll('.hot-picks__card');
    this.current   = 0;
    this.cardWidth = () => (this.cards[0]?.offsetWidth || 280) + parseFloat(getComputedStyle(this.track).gap || '20');
    this.maxIdx    = () => Math.max(0, this.cards.length - this._visible());
    this._visible  = () => Math.floor(this.track.parentElement.offsetWidth / this.cardWidth()) || 1;
    this.prevBtn?.addEventListener('click', () => this._go(this.current - 1));
    this.nextBtn?.addEventListener('click', () => this._go(this.current + 1));
    window.addEventListener('resize', () => this._go(this.current));
    this._go(0);
    this._bindTouch();
  }
  _go(idx) {
    this.current = Math.max(0, Math.min(idx, this.maxIdx()));
    this.track.style.transform = `translateX(-${this.current * this.cardWidth()}px)`;
    this.prevBtn?.classList.toggle('is-disabled', this.current === 0);
    this.nextBtn?.classList.toggle('is-disabled', this.current >= this.maxIdx());
  }
  _bindTouch() {
    const track = this.track;
    let startX = 0, startY = 0, isDragging = false;
    const THRESHOLD = 40;

    track.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = false;
      track.style.transition = 'none';
    }, { passive: true });

    track.addEventListener('touchmove', e => {
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!isDragging && Math.abs(dy) > Math.abs(dx)) {
        track.style.transition = '';
        return;
      }
      isDragging = true;
      const base = this.current * this.cardWidth();
      const clamp = Math.max(-track.parentElement.offsetWidth * 0.8, Math.min(track.parentElement.offsetWidth * 0.8, dx));
      track.style.transform = `translateX(${-(base - clamp)}px)`;
    }, { passive: true });

    track.addEventListener('touchend', e => {
      track.style.transition = '';
      if (!isDragging) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > THRESHOLD) {
        this._go(dx < 0 ? this.current + 1 : this.current - 1);
      } else {
        this._go(this.current);
      }
      isDragging = false;
    }, { passive: true });
  }
}

// ── Fade-in observer ───────────────────────────
function initFadeIn() {
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); } });
  }, { threshold: 0.1, rootMargin: '50px' });
  document.querySelectorAll('.product-card:not(.is-visible)').forEach(el => obs.observe(el));
}

// ── Spin keyframe (used inline for add-to-cart spinner) ──
const spinStyle = document.createElement('style');
spinStyle.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
document.head.appendChild(spinStyle);

// ── Init ───────────────────────────────────────
function updateWishlistCount() {
  const badge = document.getElementById('wishlist-header-count');
  if (!badge) return;
  try {
    const count = JSON.parse(localStorage.getItem('yobha_wishlist') || '[]').length;
    if (count > 0) { badge.textContent = count; badge.style.display = ''; }
    else { badge.style.display = 'none'; }
  } catch(e) { badge.style.display = 'none'; }
}
window.updateWishlistCount = updateWishlistCount;

// ── Reel Slider ────────────────────────────────
function initReelSlider() {
  document.querySelectorAll('[data-reel-slider-section]').forEach(section => {
    if (section.dataset.reelSliderReady === 'true') return;
    section.dataset.reelSliderReady = 'true';

    const track = section.querySelector('[data-reel-track]');
    const prevBtn = section.querySelector('[data-reel-prev]');
    const nextBtn = section.querySelector('[data-reel-next]');
    const modal = section.querySelector('[data-reel-modal]');
    const modalVideo = section.querySelector('[data-reel-modal-video]');
    const cards = track?.querySelectorAll('[data-reel-card]') || [];
    if (!track || !cards.length || !modal || !modalVideo) return;

    const inlineVideos = [...track.querySelectorAll('[data-reel-video]')];

    // Force-play all inline videos; autoplay can be blocked until JS touches them.
    inlineVideos.forEach(video => {
      video.muted = true;
      video.play().catch(() => {});
    });

    const getGap = () => {
      const styles = window.getComputedStyle(track);
      return parseFloat(styles.columnGap || styles.gap || '0');
    };

    const cardW = () => cards[0].offsetWidth + getGap();
    let idx = 0;
    const max = () => Math.max(0, cards.length - Math.floor(track.parentElement.offsetWidth / cardW()));

    function updateArrows() {
      prevBtn?.classList.toggle('is-disabled', idx === 0);
      nextBtn?.classList.toggle('is-disabled', idx >= max());
    }

    function slideTo(i) {
      idx = Math.max(0, Math.min(i, max()));
      track.style.transform = `translateX(-${idx * cardW()}px)`;
      updateArrows();
    }

    function syncInlinePlayback(shouldPlay) {
      inlineVideos.forEach(video => {
        if (shouldPlay) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    function openModal(card) {
      const sourceVideo = card.querySelector('[data-reel-video]');
      if (!sourceVideo) return;

      modalVideo.innerHTML = sourceVideo.innerHTML;
      modalVideo.load();
      modalVideo.removeAttribute('controls');
      modal.hidden = false;
      document.body.classList.add('overflow-hidden');
      syncInlinePlayback(false);
      modalVideo.currentTime = 0;
      modalVideo.play().catch(() => {});
      modalVideo.addEventListener('click', () => modalVideo.setAttribute('controls', ''), { once: true });
    }

    function closeModal() {
      if (modal.hidden) return;
      modalVideo.pause();
      modalVideo.removeAttribute('src');
      modalVideo.innerHTML = '';
      modal.hidden = true;
      document.body.classList.remove('overflow-hidden');
      syncInlinePlayback(true);
    }

    prevBtn?.addEventListener('click', () => slideTo(idx - 1));
    nextBtn?.addEventListener('click', () => slideTo(idx + 1));
    window.addEventListener('resize', () => slideTo(idx));
    updateArrows();

    // Touch swipe with live drag
    let swipeStartX = 0, swipeStartY = 0, isSwiping = false;
    const SWIPE_THRESHOLD = 40;

    track.addEventListener('touchstart', e => {
      swipeStartX = e.touches[0].clientX;
      swipeStartY = e.touches[0].clientY;
      isSwiping = false;
      track.style.transition = 'none';
    }, { passive: true });

    track.addEventListener('touchmove', e => {
      const dx = e.touches[0].clientX - swipeStartX;
      const dy = e.touches[0].clientY - swipeStartY;
      if (!isSwiping && Math.abs(dy) > Math.abs(dx)) {
        track.style.transition = '';
        return;
      }
      isSwiping = true;
      e.preventDefault();
      const cw = cardW();
      const base = idx * cw;
      const clamp = Math.max(-track.parentElement.offsetWidth * 0.8, Math.min(track.parentElement.offsetWidth * 0.8, dx));
      track.style.transform = `translateX(${-(base - clamp)}px)`;
    }, { passive: false });

    let didSwipe = false;
    track.addEventListener('touchend', e => {
      track.style.transition = '';
      const dx = e.changedTouches[0].clientX - swipeStartX;
      if (isSwiping && Math.abs(dx) > SWIPE_THRESHOLD) {
        didSwipe = true;
        slideTo(dx < 0 ? idx + 1 : idx - 1);
      } else {
        didSwipe = false;
        slideTo(idx);
      }
      isSwiping = false;
    }, { passive: true });

    cards.forEach(card => {
      card.addEventListener('click', () => {
        if (didSwipe) { didSwipe = false; return; }
        openModal(card);
      });
    });

    modal.querySelectorAll('[data-reel-close]').forEach(el => {
      el.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') closeModal();
    });
  });
}

// ── Desktop Nav Dropdowns ──────────────────────────────────────
function initDesktopNav() {
  const navItems = document.querySelectorAll('[data-nav-item]');
  if (!navItems.length) return;

  navItems.forEach(item => {
    const trigger  = item.querySelector('[data-nav-trigger]');
    const dropdown = item.querySelector('[data-nav-dropdown]');
    if (!dropdown) return;

    let closeTimer;

    function openDropdown() {
      clearTimeout(closeTimer);
      // Close all other open dropdowns
      navItems.forEach(other => {
        if (other === item) return;
        other.classList.remove('is-open');
        const od = other.querySelector('[data-nav-dropdown]');
        const ot = other.querySelector('[data-nav-trigger]');
        if (od) od.classList.remove('is-visible');
        if (ot) ot.setAttribute('aria-expanded', 'false');
      });
      item.classList.add('is-open');
      dropdown.classList.add('is-visible');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    function closeDropdown() {
      closeTimer = setTimeout(() => {
        item.classList.remove('is-open');
        dropdown.classList.remove('is-visible');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }, 80);
    }

    item.addEventListener('mouseenter', openDropdown);
    item.addEventListener('mouseleave', closeDropdown);
    dropdown.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    dropdown.addEventListener('mouseleave', closeDropdown);
  });

  // Close all on outside click
  document.addEventListener('click', e => {
    if (e.target.closest('[data-nav-item]')) return;
    navItems.forEach(item => {
      item.classList.remove('is-open');
      const d = item.querySelector('[data-nav-dropdown]');
      const t = item.querySelector('[data-nav-trigger]');
      if (d) d.classList.remove('is-visible');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  });
}

function fixRupeeSymbol(root) {
  const walker = document.createTreeWalker(root || document.body, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach(n => {
    if (n.nodeValue.includes('Rs.')) {
      n.nodeValue = n.nodeValue.replace(/Rs\.\s?/g, '₹');
    }
  });
}

function initPdpTabs() {
  const nav = document.querySelector('.pdp-tabs__nav');
  if (!nav) return;
  nav.addEventListener('click', e => {
    const btn = e.target.closest('.pdp-tab');
    if (!btn) return;
    const tab = btn.dataset.tab;
    nav.querySelectorAll('.pdp-tab').forEach(b => {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
    });
    document.querySelectorAll('.pdp-tab-panel').forEach(p => {
      p.classList.toggle('is-active', p.id === 'tab-' + tab);
    });
  });
}

// ── Fixed header offset ───────────────────────────────────────
function setHeaderOffset() {
  const header = document.getElementById('site-header');
  const main   = document.getElementById('MainContent');
  if (!header || !main) return;
  main.style.paddingTop = header.offsetHeight + 'px';
}

function initHeaderOffset() {
  setHeaderOffset();
  window.addEventListener('resize', setHeaderOffset);
  // ResizeObserver keeps the offset accurate whenever the header changes height
  // (e.g. announcement bar wraps, fonts load, viewport rotates)
  const hdr = document.getElementById('site-header');
  if (typeof ResizeObserver !== 'undefined' && hdr) {
    new ResizeObserver(setHeaderOffset).observe(hdr);
  }
}

async function restoreSavedCart() {
  const saved = sessionStorage.getItem('yobha_saved_cart');
  const buyNowVariant = sessionStorage.getItem('yobha_buy_now_variant');
  if (!saved || !buyNowVariant) return;
  sessionStorage.removeItem('yobha_saved_cart');
  sessionStorage.removeItem('yobha_buy_now_variant');
  try {
    const items = JSON.parse(saved);
    if (!items.length) return;
    // Remove only the buy-now item from cart, add back saved items
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [parseInt(buyNowVariant, 10)]: 0 } })
    });
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
  } catch(e) {}
}

function initImageZoom() {
  document.querySelectorAll('.pmd-main__slide').forEach(slide => {
    const img = slide.querySelector('img');
    if (!img) return;
    let active = false;

    slide.addEventListener('mouseenter', () => {
      active = true;
      img.style.transform = 'scale(2)';
    });

    slide.addEventListener('mousemove', (e) => {
      if (!active) return;
      const rect = slide.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
    });

    slide.addEventListener('mouseleave', () => {
      active = false;
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = 'center';
    });
  });
}

class ProductLightbox {
  constructor() {
    this.overlay = document.getElementById('pmd-lightbox');
    if (!this.overlay) return;
    this.img     = document.getElementById('pmd-lb-img');
    this.inner   = document.getElementById('pmd-lb-inner');
    this.prevBtn = document.getElementById('pmd-lb-prev');
    this.nextBtn = document.getElementById('pmd-lb-next');
    this.closeBtn = document.getElementById('pmd-lb-close');
    this.images  = [];
    this.current = 0;
    this._collectImages();
    this._bindTriggers();
    this._bindControls();
  }

  _collectImages() {
    document.querySelectorAll('.pmd-main__slide img').forEach(img => this.images.push(img.src));
    if (!this.images.length) {
      document.querySelectorAll('.pms-slide img').forEach(img => this.images.push(img.src));
    }
  }

  _hiRes(src) {
    return src.replace(/(width=)\d+/, '$12400');
  }

  _bindTriggers() {
    document.querySelectorAll('.pmd-main__slide img').forEach((img, i) => {
      img.addEventListener('click', () => this.open(i));
    });
    document.querySelectorAll('.pms-slide img').forEach((img, i) => {
      img.addEventListener('click', () => this.open(i));
    });
  }

  _bindControls() {
    this.closeBtn.addEventListener('click', () => this._close());
    this.prevBtn.addEventListener('click', (e) => { e.stopPropagation(); this._go(this.current - 1); });
    this.nextBtn.addEventListener('click', (e) => { e.stopPropagation(); this._go(this.current + 1); });
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay || e.target === this.inner) this._close();
    });
    document.addEventListener('keydown', (e) => {
      if (!this.overlay.classList.contains('is-open')) return;
      if (e.key === 'Escape')      this._close();
      if (e.key === 'ArrowLeft')   this._go(this.current - 1);
      if (e.key === 'ArrowRight')  this._go(this.current + 1);
    });
  }

  open(index) {
    this.current = index;
    this._load();
    this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  _close() {
    this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  _go(index) {
    const n = this.images.length;
    if (!n) return;
    this.current = (index + n) % n;
    this._load();
  }

  _load() {
    const src = this.images[this.current];
    if (!src) return;
    this.img.src = this._hiRes(src);
    this.img.alt = `Product image ${this.current + 1}`;
    const single = this.images.length <= 1;
    this.prevBtn.hidden = single;
    this.nextBtn.hidden = single;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Run first — layout depends on this; must never be blocked by other failures
  initHeaderOffset();

  const safe = (label, fn) => { try { fn(); } catch (e) { console.error('[YOBHA]', label, e); } };

  safe('Sidebar',          () => new Sidebar());
  safe('SearchDrawer',     () => new SearchDrawer());
  safe('AccountDropdown',  () => new AccountDropdown());
  document.querySelectorAll('.hero-slider').forEach(el => safe('HeroSlider', () => new HeroSlider(el)));
  document.querySelectorAll('.product-card').forEach(el => safe('ProductCard', () => new ProductCard(el)));
  safe('initFadeIn',       () => initFadeIn());
  if (document.getElementById('product-form')) safe('ProductPage', () => new ProductPage());
  safe('initHotPicks',     () => initHotPicks());
  safe('initReelSlider',   () => initReelSlider());
  safe('initDesktopNav',   () => initDesktopNav());
  safe('initPdpTabs',      () => initPdpTabs());
  if (document.getElementById('pmd-lightbox')) safe('ProductLightbox', () => new ProductLightbox());
  safe('initImageZoom', () => initImageZoom());
  if (document.getElementById('cart-items-wrap')) {
    restoreSavedCart().then(() => safe('CartPage', () => new CartPage()));
  }
  safe('fetchCartCount',      () => fetchCartCount());
  safe('updateWishlistCount', () => updateWishlistCount());
  safe('fixRupeeSymbol',      () => fixRupeeSymbol());

  // Watch for dynamically injected content (cart, search, recommendations)
  safe('MutationObserver', () => {
    new MutationObserver(mutations => {
      mutations.forEach(m => m.addedNodes.forEach(node => {
        if (node.nodeType === 1) safe('fixRupeeSymbol:mutation', () => fixRupeeSymbol(node));
      }));
    }).observe(document.body, { childList: true, subtree: true });
  });
});
